import { queryOptions } from '@tanstack/react-query'
import { apiGet, apiPost } from './client'

/**
 * Settings → Easeedesk → Predefined messages si Template messages.
 *
 * Perechile vechi: `chat_predefined_messages`, `chat_predefined_messages_list`,
 * `chat_predefined_message`, `save_predefined_message`,
 * `edit_predefined_message`, `remove_predefined_message`,
 * `collaborate_template_messages`, `collaborate_list_template`,
 * `save_collaborate_template`, `edit_collaborate_template`,
 * `remove_collaborate_template`.
 *
 * `apiGet` / `apiPost` pun ELE `/api/v2` in fata caii.
 */

export interface PredefinedMessageRow {
  id: number
  company_id: number | null
  company: string
  active: number
  message: string
}

export interface Translation {
  language_id: number
  lang: string
  message: string
}

export interface CollaborateTemplate {
  id: number
  company_id: number | null
  title: string
  message: string
}

const PREDEFINED = '/settings/easeedesk/chat_predefined_messages'
const COLLABORATE = '/settings/easeedesk/collaborate_template_messages'

export function predefinedShellQuery() {
  return queryOptions({
    queryKey: ['settings', 'easeedesk', 'chat_predefined_messages'],
    queryFn: () =>
      apiGet<{
        title: string
        moduleUrl: string
        companies: Array<{ ID: number; name: string }>
        languages: Array<{ id: number; language_name: string; short_name: string }>
      }>(PREDEFINED),
  })
}

export function predefinedListQuery() {
  return queryOptions({
    queryKey: ['settings', 'easeedesk', 'chat_predefined_messages', 'list'],
    queryFn: () => apiGet<{ messages: PredefinedMessageRow[] }>(`${PREDEFINED}/list`),
  })
}

export function predefinedMessageQuery(message: number) {
  return queryOptions({
    queryKey: ['settings', 'easeedesk', 'chat_predefined_messages', 'one', message],
    queryFn: () =>
      apiGet<{ id: number; company_id: number | null; active: number; translations: Translation[] }>(
        `${PREDEFINED}/${message}`,
      ),
    enabled: message > 0,
  })
}

export function createPredefinedMessage(company: number, messages: Record<number, string>) {
  const fields: Record<string, string | number> = { company }
  for (const [languageId, text] of Object.entries(messages)) fields[`message[${languageId}]`] = text
  return apiPost<{ ok?: boolean; id?: number }>(PREDEFINED, fields)
}

export function updatePredefinedTranslation(message: number, language: number, body: string) {
  return apiPost<{ ok?: boolean }>(`${PREDEFINED}/${message}`, {
    message_language: language,
    message_body: body,
  })
}

export function setPredefinedMessageActive(message: number, active: boolean) {
  return apiPost<{ ok?: boolean }>(`${PREDEFINED}/${message}/active`, { value: active ? 1 : 0 })
}

export function collaborateShellQuery() {
  return queryOptions({
    queryKey: ['settings', 'easeedesk', 'collaborate_template_messages'],
    queryFn: () =>
      apiGet<{ title: string; moduleUrl: string; companies: Array<{ ID: number; name: string }> }>(COLLABORATE),
  })
}

export function collaborateListQuery(company: number) {
  return queryOptions({
    queryKey: ['settings', 'easeedesk', 'collaborate_template_messages', 'list', company],
    queryFn: () => apiGet<{ templates: CollaborateTemplate[] }>(`${COLLABORATE}/list?company=${company}`),
  })
}

export function createCollaborateTemplate(company: number, title: string, message: string) {
  return apiPost<{ ok?: boolean; id?: number }>(COLLABORATE, { company, title, message_body: message })
}

export function updateCollaborateTemplate(template: number, company: number, title: string, message: string) {
  return apiPost<{ ok?: boolean }>(`${COLLABORATE}/${template}`, { company, title, message_body: message })
}

export function removeCollaborateTemplate(template: number) {
  return apiPost<{ ok?: boolean }>(`${COLLABORATE}/${template}/remove`, {})
}
